import React, { FC, useState, useEffect, useContext, Fragment } from 'react';

import Accordion from 'b2b-ui-components-lib/lib/components/accordion';

import InstallationDetails from 'featureLib/InstallationDetailsAOI';

import AddressResolutionComponent from 'home/view/manage-feasibility/pages/AddressResolutionComponent';
import CancelModal from 'home/view/manage-feasibility/components/molecules/CancelModal';

import { getInstallationDetailsAccordionHeading, getFileFormatErrorTemplate, getFileUploadErrorTemplate } from 'home/view/check-feasibility/comp-generator/component.generator';
import { DedicatedRouteProps, ProgressEventType, FileTypes } from 'home/view/check-feasibility/comp-generator/common-interface';

import { downloadSampleFile, uploadFile, downloadErrorFile, downloadUpgradeTemplate, deleteLead } from './service';
import { setInSessionStorage, isTMORedirect, getFromSessionStorage, redirectToATBDashboardPage } from 'common/utils/app.utils';
import { MODULE_ID, INVALID_API_RESPONSES } from 'home/common/constants/constants';
import { MULTI_SITE_MODULE_ID, LEAD_ID, DEFAULT_TOAST_MSG, ERROR_TOAST, AUTH_TOKEN } from 'home/view/manage-feasibility/constants';
import { STORAGE_KEY, isBulkUpgrade } from 'common/constants/app.constants';
import { AppContext } from 'App';
interface FileUploadCbProps {
  fileList: FileList;
  error: string;
  setFileError: React.Dispatch<React.SetStateAction<string | React.ReactNode>>;
  setDownloadPercentage: React.Dispatch<React.SetStateAction<number>>;
}

interface ExtendedFileUploadCbProps extends FileUploadCbProps {
  setIsSubmitDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}

export const InstallationDetailsAccordion: FC<DedicatedRouteProps> = (props) => {
  const { isdedicatedJourney, history } = props;

  const { setToast, setMultiSiteFileUploadSuccess } = useContext<any>(AppContext);

  const abortController: AbortController = new AbortController();

  const [showAddressResolutionSection, setShowAddressResolutionSection] = useState<boolean>(false);
  const [showCanceLeadPopup, setShowCancelLeadPopup] = useState<boolean>(false);

  const handleDownloadClick = (setIsDownloadDisabled: React.Dispatch<React.SetStateAction<boolean>>): void => {
    downloadSampleFile({ isdedicatedJourney, setToast, setIsDownloadDisabled });
  };

  const handleDownloadUpgradeTemplateClick = (setIsDownloadDisabled: React.Dispatch<React.SetStateAction<boolean>>): void => {
    if (isBulkUpgrade()) {
      downloadUpgradeTemplate({ isdedicatedJourney, setToast, setIsDownloadDisabled });
    } else {
      downloadSampleFile({ isdedicatedJourney, setToast, setIsDownloadDisabled });
    }
  };

  const setGlobalData = (flag: boolean) => {
    setInSessionStorage(MODULE_ID, STORAGE_KEY.JOURNEY_TYPE, flag);
    setMultiSiteFileUploadSuccess(flag);
  };

  const handleFileUpload = ({ fileList, error, setFileError, setDownloadPercentage, setIsSubmitDisabled }: ExtendedFileUploadCbProps): void => {
    if (error) {
      setGlobalData(false);
      setToast(true, error, ERROR_TOAST);
      return;
    }

    const onUploadProgress = (progressEvent: ProgressEventType) => {
      const loadingPercentage = (progressEvent.loaded / progressEvent.total) * 100;
      setDownloadPercentage(loadingPercentage);
      setIsSubmitDisabled(true);
    };

    const onUploadFormatError = (errorTemplate: React.ReactNode) => {
      setFileError(errorTemplate);
      setDownloadPercentage(0);
      setIsSubmitDisabled(true);
      setGlobalData(false);
      setToast(true, getFileUploadErrorTemplate(errorTemplate), ERROR_TOAST);
    };

    const onUploadFail = (errorMsg: any) => {
      setFileError(errorMsg);
      setDownloadPercentage(0);
      setIsSubmitDisabled(true);
      setGlobalData(false);
      setToast(true, errorMsg, ERROR_TOAST);

      // redirect to ATB Dashboard if invalid token
      if (errorMsg?.includes(INVALID_API_RESPONSES.INVALID_TOKEN) || errorMsg?.includes(INVALID_API_RESPONSES.MISSING_TOKEN)) {
        redirectToATBDashboardPage();
      }
    };

    const onUploadSuccess = (): void => {
      setIsSubmitDisabled(false);
      setShowAddressResolutionSection(true);
      setGlobalData(true);
    };

    const handleErrorSheetDownload = ({ file, errorSheetUrl }: { file: any; errorSheetUrl: string }): void => {
      const errorTemplate = getFileFormatErrorTemplate(file, () => downloadErrorFile(errorSheetUrl, setToast));

      if (errorTemplate) {
        onUploadFormatError?.(errorTemplate);
      }
    };

    try {
      Array.from(fileList).forEach(async (file: any) => {
        const { res, err } = await uploadFile({ file, abortController, onUploadProgress });
        if (res) {
          const {
            data: {
              success,
              result: { leadId, errorSheetUrl, error }
            }
          } = res;

          if (!error) {
            setInSessionStorage(MULTI_SITE_MODULE_ID, LEAD_ID, leadId);
          }

          if (success) {
            onUploadSuccess?.();
          } else if (errorSheetUrl) {
            handleErrorSheetDownload({ file, errorSheetUrl });
          }
        } else if (err) {
          const errorMsg = (err && err.response && err.response.data && err.response.data.errorMsg) || DEFAULT_TOAST_MSG;
          onUploadFail?.(errorMsg);
          throw new Error(errorMsg);
        }
      });
    } catch (e) {
      console?.error(e);
    }
  };

  const handleCancelClick = () => {
    setShowCancelLeadPopup(true);
  };

  const handleCancelLead = (remarks: string): void => {
    deleteLead(remarks, isdedicatedJourney, setToast, history);
  };

  useEffect(() => {
    const isAddressResolutionStage = getFromSessionStorage(MULTI_SITE_MODULE_ID, LEAD_ID) ?? null;

    if (isTMORedirect || isAddressResolutionStage) {
      setShowAddressResolutionSection(true);
      setGlobalData(true);
    }
  }, []);

  if (showAddressResolutionSection) {
    return (
      <Accordion title={getInstallationDetailsAccordionHeading(true)} variant="round" isExpanded={true}>
        <AddressResolutionComponent {...props} />
      </Accordion>
    );
  }

  return (
    <Fragment>
      {showCanceLeadPopup && <CancelModal setshowPopup={setShowCancelLeadPopup} onSubmit={handleCancelLead} />}
      <InstallationDetails
        OnDownloadClick={handleDownloadClick}
        onDownloadTemplate={handleDownloadUpgradeTemplateClick}
        format={FileTypes.xlsx}
        size={1}
        onUploadCb={handleFileUpload}
        abortController={abortController}
        onCancelClick={handleCancelClick}
      />
    </Fragment>
  );
};

export default InstallationDetailsAccordion;
